import {VNode} from 'snabbdom';
import {AttrsSchema, Component} from 'panel';
import findIndex from 'lodash/findIndex';
import throttle from 'lodash/throttle';
import partition from 'lodash/partition';
import sum from 'lodash/sum';
import ResizeObserver from 'resize-observer-polyfill';

import {ComponentLifetimeEndEventName, useEventListener} from 'common/util/component-helpers';
import {truncateMiddle} from 'common/util/string';
import {emitInfraTrackEvent, getCustomElementParents, SHIFT_KEY_CODE} from 'common/util/dom';
import {getModifierKeys} from 'common/util/keyboard';
import {stickyElemHooks} from 'common/util/dom-sticky-stack';
import {fuzzyMatchSanitized, matchPartsFromSegments} from 'common/util/fuzzy-match';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {getNow, PerformanceEventPrefix} from 'common/util/timing';
import {LoadingType} from 'common/util/workflow-timing';
import {panel2react} from 'common/util/react';

import {TrackingEvent} from './tracking';
import {template} from './template';
import css from './index.inline.styl';
import {
  CellClickEvent,
  CellRenderContext,
  ColumnMovedEvent,
  ColumnResizeEvent,
  DisplayTableRow,
  InitialColumnResizeState,
  NumResultsChangeEvent,
  ResizedColumnWidths,
  RowClickEvent,
  RowDetailsVisibilityChangeEvent,
  SelectionChangeEvent,
  SortChangeEvent,
  TableColumn,
  TableRow,
  TableScrollEvent,
  TableState,
} from './types';

const COMPONENT_NAME = `mp-table`;
const PROGRESSIVE_SIZE_ROWS = 25;
// TODO: Dynamically calculate based on available height
const INITIAL_NUM_DISPLAY_ROWS = PROGRESSIVE_SIZE_ROWS * 2;
export const PROGRESSIVE_HEIGHT_BUFFER_PX = 1750;
const COLUMN_TITLE_MAX_LENGTH = 20;

enum DROP_POSITION {
  LEFT,
  RIGHT,
}

interface TableJsxProps {
  attrs?: {
    'allow-x-overflow'?: boolean;
    loading?: boolean;
    'multi-select'?: boolean;
    'selection-limit'?: number;
    'empty-message'?: string;
    'search-filter'?: string;
    'default-sort-field'?: string;
    'default-sort-order'?: number;
    'max-body-height'?: string;
    'num-sticky-rows'?: number;
    'sticky-titles'?: boolean;
    'disable-page-sticky-titles'?: boolean;
    'disable-clickable-rows'?: boolean;
    'disable-expandable-rows'?: boolean;
    'min-row-count'?: number;
    'initial-num-rows-to-display'?: number;
    'row-focused-context'?: boolean;
    'track-name'?: string;
    'window-scroll'?: boolean;
    'draggable-columns'?: boolean;
    'draggable-after-index'?: number;
    'hide-titles'?: boolean;
    'disable-auto-sorting'?: boolean;
    'resized-column-widths'?: ResizedColumnWidths;
  };
  props?: {
    columns?: TableColumn[];
    rows?: TableRow[];
    rowDetailsRenderer?: (TableRow) => VNode;
  };
  on?: {
    selectionChange?: (ev: SelectionChangeEvent) => void;
    rowClick?: (ev: RowClickEvent<TableRow>) => void;
    cellClick?: (ev: CellClickEvent<TableRow>) => void;
    sortChange?: (ev: SortChangeEvent<TableRow>) => void;
    numResultsChange?: (ev: NumResultsChangeEvent) => void;
    rowDetailsVisibilityChange?: (ev: RowDetailsVisibilityChangeEvent) => void;
    columnMoved?: (ev: ColumnMovedEvent) => void;
    tableScroll?: (ev: TableScrollEvent) => void;
  };
}

export class Table extends Component<TableState, TableJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<TableJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'allow-x-overflow': {
        type: `boolean`,
        description: `Whether to allow the table contents to overflow and become scrollable (vs resizing the rows to fit)`,
      },
      loading: {
        type: `boolean`,
      },
      'multi-select': {
        type: `boolean`,
        description: `Show checkboxes besides each row and allow select all / none of current filtered results`,
      },
      'selection-limit': {
        type: `number`,
        description: `Limit the number of selections allowed. Also disables shift-click.`,
      },
      'empty-message': {
        type: `string`,
        default: `This table is empty. Let's make a fresh start`,
      },
      'search-filter': {
        type: `string`,
        description: `Only display rows that match the search-filter and highlight the matches`,
      },
      'default-sort-field': {
        type: `string`,
        description: `Field by which table should be initially sorted. Updating this attribute will reset the sort to default-sort-field.`,
      },
      'default-sort-order': {
        type: `number`,
        default: 1,
        description: `Initial sort order, 1 for ascending, -1 for descending`,
      },
      'disable-expandable-rows': {
        type: `boolean`,
        description: `Whether to disable the expandable rows despite the presence of a renderer for it`,
      },
      'max-body-height': {
        type: `string`,
        description: `Max body height with units, more rows are loaded as user scrolls progressively`,
      },
      'num-sticky-rows': {
        type: `number`,
        default: 0,
        description: `Sticky rows that will not be sorted, that are to be placed at the start of the table. These rows are to be indexed by [0, 1, ..., num-sticky-rows) in the table rows passed in by the user. By default, all rows are sortable.`,
      },
      'sticky-titles': {
        type: `boolean`,
        description: `apply sticky positioning to the component and to the whole page`,
      },
      'disable-page-sticky-titles': {
        type: `boolean`,
        description: `apply sticky positioning to the component, but not to the whole page`,
      },
      'disable-clickable-rows': {
        type: `boolean`,
        description: `Rows do not show cursor pointer, change bg color on hover or fire clickRow events`,
      },
      'min-row-count': {
        type: `number`,
        default: 0,
        description: `The minimum number of rows to be displayed. If under, blank rows will be appended to the table`,
      },
      'initial-num-rows-to-display': {
        type: `number`,
        description: `The initial number of rows to display. Overrides the default of 50.`,
      },
      'row-focused-context': {
        type: `boolean`,
        description: `Cell renderers are given isRowFocused as render context.
          Turning this on makes an update per row hover.
          Use it to conditionally render perf intensive components`,
      },
      'track-name': {
        type: `string`,
        description: `If specified, dispatches '[mp-table] <event>' bubbled events that can be listened for tracking with name as a property`,
      },
      'window-scroll': {
        type: `boolean`,
        description: `Assign progressive scroll handler to window instead of table body`,
      },
      'draggable-columns': {
        type: `boolean`,
        description: `True if it allows re-order columns with drag and drop`,
      },
      'draggable-after-index': {
        type: `number`,
        description: `Prevents dragging and dropping for columns less than the given index`,
      },
      'hide-titles': {
        type: `boolean`,
        description: `Do not show titles for the table`,
      },
      'disable-auto-sorting': {
        type: `boolean`,
        description: `If it is true, mp-table won't re-order the rows when the user clicks the sort controllers, but it still triggers the sort events`,
      },
      'resized-column-widths': {
        type: `json`,
        description: `The widths of any resized columns.`,
        tsType: `ResizedColumnWidths`,
      },
    };
  }

  private tableBodyEl: HTMLElement;
  private displayRowsCache: void | DisplayTableRow[];
  private isMouseOver: boolean;
  private loadingStartTimeMs: number;
  private focusedRowIdx: number;
  private lastSelectedRowIdx: number;
  private numResults: number;
  private loadingStarted: number;
  private connectedAt: number;
  private initialRenderDone = false;
  private onResizePointerMove: (ev: PointerEvent) => void;
  private onResizePointerUp: (ev: PointerEvent) => void;
  private resetResizeState: () => void;
  private distributeResizableColumns = throttle(this._distributeResizableColumns.bind(this), 200);
  private recalculateResizeHandlePositions = throttle(this._recalculateResizeHandlePositions.bind(this), 200);

  constructor() {
    super();
    this.loadingStartTimeMs = getNow(); // unix milliseconds when loading started
    this.tableBodyEl = null; // table body dom element reference
    this.displayRowsCache = null; // cache of filtered + sorted rows, so scrolling is super smooth
    this.focusedRowIdx = null;
    this.isMouseOver = false; // so we can only listen for shift up/down when mouse is over the table
    this.lastSelectedRowIdx = null; // for shift+click range select
    this.numResults = -1; // so we can fire numResultsChange event
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        rows: [],
        rowDetailsRenderer: null,
        columns: [],
        doingRangeSelect: false,
        draggingColumnIndex: null,
        draggingOverColumnIndex: null,
        dropPosition: null,
        focusedRowIdx: null,
        hasBodyScrolled: false,
        isDetailHovered: false,
        numRowsToDisplay: INITIAL_NUM_DISPLAY_ROWS,
        sortField: null,
        sortOrder: 1,
        resizeColumnIndex: null,
      },
      helpers: {
        COLUMN_TITLE_MAX_LENGTH,
        stickyElemHooks,
        truncateMiddle,

        handleTableLoading: () => {
          this.loadingStarted = getNow();
        },
        handleTableLoaded: () => {
          const parents = getCustomElementParents(this);

          emitInfraTrackEvent(this, {
            eventName: ComponentLifetimeEndEventName.LoadingDisplayed,
            eventPrefix: PerformanceEventPrefix,
            props: {
              type: LoadingType.Table,
              elapsedMs: getNow() - this.loadingStarted,
              parentPath: parents.join(`>`),
              parents,
            },
          });
        },

        handleInitialRender: () => {
          if (!this.initialRenderDone) {
            this.initialRenderDone = true;
            const parents = getCustomElementParents(this);

            emitInfraTrackEvent(this, {
              eventName: `Table Displayed`,
              eventPrefix: PerformanceEventPrefix,
              props: {
                elapsedMs: getNow() - this.connectedAt,
                parentPath: parents.join(`>`),
                parents,
              },
            });
          }
        },

        getRowsToDisplay: (): DisplayTableRow[] => this._getDisplayRows().slice(0, this.state.numRowsToDisplay),

        getCellLabel: (row: TableRow, col: TableColumn): string => this._getCellLabel(row, col),

        getColumnType: (row: TableRow, col: TableColumn) =>
          col.type || (col.typeTransform && col.typeTransform(row, col.field)) || (col.cellRenderer ? `` : `text`),

        getCellRenderContexts: (
          cols: TableColumn[],
          row: DisplayTableRow,
          rowIdx: number,
          totalRows: number,
        ): CellRenderContext[] => {
          return cols.map((col, colIdx) => this.helpers.getCellRenderContext(row, col, colIdx, rowIdx, totalRows));
        },

        getCellRenderContext: (
          row: DisplayTableRow,
          col: TableColumn,
          columnIdx: number,
          rowIdx: number,
          totalRows: number,
        ): CellRenderContext => ({
          field: col.field,
          isRowFocused: this.state.focusedRowIdx === rowIdx,
          label: this.helpers.getCellLabel(row.val, col),
          matchParts: row.matchParts && row.matchParts[col.field],
          totalRows,
          columnIdx,
          rowIdx,
        }),

        getColumnsToDisplay: (): TableColumn[] => {
          // set rowDetailsRenderer will add a special collapsible caret column on the left with isExpanded as the field
          const collapsibleColumn = this.hasExpandableRows() ? [{field: `isExpanded`, type: `collapsible`}] : [];
          // multi-select adds a special checkbox column on the left with isSelected as the field
          const checkboxColumn = this.attr(`multi-select`)
            ? [{field: `isSelected`, type: `checkbox`, computedWidth: 62}]
            : [];
          const columns = this.state.columns;
          return [...collapsibleColumn, ...checkboxColumn, ...columns];
        },

        getGroupedColIndices: (
          cols: TableColumn[],
          prevContexts: CellRenderContext[],
          currContexts: CellRenderContext[],
        ): Set<number> => {
          // group-by logic
          //    COL1  COL2  COL3   -> COL1  COL2  COL3
          //      A|    B|    C|   ->   A|    B|    C͟|
          //      A|    B|    D|   ->    |    _|    D͟|
          //      A|    D|    D|   ->   _     D͟|    D͟|
          //      B|    D|    D|   ->   B     D͟|    D͟|
          const groupedColIndices = new Set<number>();
          if (prevContexts === null) {
            return groupedColIndices;
          }
          let addIfNextGroupBy = null;
          const rows = this.getDisplayedRows();
          const getRowValue = (context) => rows[context.rowIdx][context.field];
          for (let colIdx = 0; colIdx < cols.length; ++colIdx) {
            const col = cols[colIdx];
            // For checkboxes it will by grouped if it is behind a grouped value
            if (col.type === `checkbox`) {
              addIfNextGroupBy = colIdx;
              continue;
            }
            if (col.isGroupable && getRowValue(prevContexts[colIdx]) === getRowValue(currContexts[colIdx])) {
              if (addIfNextGroupBy !== null) {
                groupedColIndices.add(addIfNextGroupBy);
              }
              groupedColIndices.add(colIdx);
            } else {
              break;
            }
          }
          return groupedColIndices;
        },
        toggleSelected: (ev, row, rowIdx) => {
          ev.stopPropagation(); // so we don't also fire the rowClick event
          if (document.getSelection) {
            document.getSelection().empty(); // so text isn't highlighted on shift click or double click
          }

          if (!row.isSelectDisabled) {
            const isSelected = !row.isSelected;
            let changed = [{isSelected, row}];

            if (this.helpers.rangeSelectAllowed() && ev.shiftKey && this.lastSelectedRowIdx !== null) {
              // shift+click multi-select behaviour is like gmail
              // change everything in between current and last clicked row to new current state
              const [sliceStartIdx, sliceEndIdx] = [rowIdx, this.lastSelectedRowIdx].sort((a, b) => a - b);
              changed = this.getDisplayedRows()
                .slice(sliceStartIdx, sliceEndIdx + 1) // endIdx + 1 because indices are inclusive
                .filter((r) => !r.isSelectDisabled && r.isSelected !== isSelected)
                .map((r) => ({isSelected, row: r}));
            }

            this.dispatchEvent(
              new CustomEvent(`selectionChange`, {
                detail: {
                  changed,
                  isToggleAll: false,
                  rows: this.getDisplayedRows(),
                },
              }) as SelectionChangeEvent,
            );
            this.lastSelectedRowIdx = rowIdx;
            this.helpers.track(TrackingEvent.SelectionChange, {isSelected, numChanged: changed.length});
          }
        },

        isColumnDraggable: (colIndex) => {
          const draggableIndex = this.attr(`draggable-after-index`);
          return !draggableIndex || colIndex > draggableIndex;
        },

        getSelectionStats: () => {
          const selectableRows = this._getSelectableDisplayedRows();
          const selectedRows = selectableRows.filter((row) => row.isSelected);
          const numSelectable = selectableRows.length;
          const numSelected = selectedRows.length;
          const limit = this.attr(`selection-limit`) || Number.POSITIVE_INFINITY;
          return {
            numSelectable,
            numSelected,
            allSelected: numSelectable > 0 && numSelected === numSelectable,
            atSelectionLimit: numSelected >= limit,
          };
        },

        getCheckboxState: (item: TableRow) => {
          if (item.isSelected) {
            const options: any = {
              attrs: {
                checked: true,
                disabled: false,
              },
            };
            if (item.checkedColor) {
              options.attrs[`checked-color`] = item.checkedColor;
            }
            if (item.checkedIcon) {
              options.attrs[`checked-icon`] = item.checkedIcon;
            }
            return options;
          }

          const {atSelectionLimit} = this.helpers.getSelectionStats();
          if (atSelectionLimit) {
            return {
              selectDisabledReason:
                item.selectDisabledReason || `You can't select more than ${this.attr(`selection-limit`)} items.`,
              attrs: {disabled: true, checked: false},
            };
          }

          return {
            selectDisabledReason: item.selectDisabledReason,
            attrs: {disabled: item.isSelectDisabled, checked: false},
          };
        },

        toggleAllSelected: () => {
          // partial -> none, none -> all, all -> none (like gmail)
          const isSelected = this.helpers.getSelectionStats().numSelected === 0;
          const changed = this._getSelectableDisplayedRows()
            .filter((row) => row.isSelected !== isSelected)
            .map((row) => ({isSelected, row}));
          this.dispatchEvent(
            new CustomEvent(`selectionChange`, {
              detail: {
                changed,
                isToggleAll: true,
                rows: this.getDisplayedRows(),
              },
            }) as SelectionChangeEvent,
          );
          this.helpers.track(TrackingEvent.SelectionChange, {isSelected, numChanged: changed.length, toggleAll: true});
        },

        handleCollapsibleCaretClick: (ev, row, rowIdx) => {
          this.dispatchEvent(
            new CustomEvent(`rowDetailsVisibilityChange`, {
              detail: {
                changed: [{isExpanded: !row.isExpanded, row, rowIdx}],
              },
            }) as RowDetailsVisibilityChangeEvent,
          );
          ev.stopPropagation();
        },

        handleCollapseAll: () => {
          this.dispatchEvent(
            new CustomEvent(`rowDetailsVisibilityChange`, {
              detail: {
                changed: this.state.rows.map((row, rowIdx) => ({isExpanded: false, row, rowIdx})),
              },
            }) as RowDetailsVisibilityChangeEvent,
          );
        },

        // NOTE: currently, range selection isn't allowed if there is a selection-limit to keep things simple.
        // restriction may be removed in the future if there is popular demand
        rangeSelectAllowed: () => !this.attr(`selection-limit`),

        willRangeSelect: (rowIdx) => {
          // provide UI feedback on what will be selected
          if (this.state.doingRangeSelect) {
            const [startIdx, endIdx] = [this.focusedRowIdx, this.lastSelectedRowIdx].sort((a, b) => a - b);
            return rowIdx >= startIdx && rowIdx <= endIdx;
          }
          return false;
        },

        handleHeaderTitleClick: (column: TableColumn) => {
          const state = this.state;

          if (column.isSortable) {
            const sortField = column.field;
            // if same column is clicked, toggle sort order, otherwise keep it same
            const sortOrder = (state.sortField === sortField ? -1 : 1) * state.sortOrder;
            const sortDetail = {sortField, sortOrder};
            this.update(sortDetail);
            this.resetScroll();

            this._emitSortChange(true);
            this.helpers.track(TrackingEvent.SortChange, {field: sortField, order: sortOrder});
          }
        },

        handleRowClick: (ev, row, rowIdx) => {
          if (!this.attr(`disable-clickable-rows`) && !row.isRowClickDisabled) {
            this.dispatchEvent(
              new CustomEvent(`rowClick`, {
                detail: {
                  row,
                  rowIdx,
                  target: ev.target,
                  modifierKeys: getModifierKeys(ev),
                },
              }) as RowClickEvent,
            );
          }
        },

        handleCellClick: (ev, row, field, context) => {
          this.dispatchEvent(
            new CustomEvent(`cellClick`, {
              detail: {
                row,
                field,
                context,
                target: ev.target,
                modifierKeys: getModifierKeys(ev),
              },
            }) as CellClickEvent,
          );
          this.helpers.track(TrackingEvent.CellClick, {field, searchFilterLen: this.attr(`search-filter`).length});
        },

        handleBodyMouseEnter: () => {
          this.isMouseOver = true;
        },

        handleBodyMouseLeave: () => {
          this.focusedRowIdx = null;
          this.isMouseOver = false;
          if (this.attr(`row-focused-context`)) {
            this.update({focusedRowIdx: this.focusedRowIdx});
          }
        },

        handleRowMouseEnter: (rowIdx: number) => {
          this.focusedRowIdx = rowIdx;
          // row-focused-context is opt-in because default row hover is done on css which is much more efficient
          // only tables that require context.focusedRowIdx to render should cause re-render per row mouse enter
          if (this.attr(`row-focused-context`)) {
            this.update({focusedRowIdx: this.focusedRowIdx});
            // eslint-disable-next-line sonarjs/no-duplicated-branches
          } else if (this.attr(`multi-select`) && this.state.doingRangeSelect) {
            // shift+click UX feedback for multi-select table requires us to re-render when row focus changes
            // we only update if user is performing a range select, otherwise it will unnecessarily slow down the table
            this.update({focusedRowIdx: this.focusedRowIdx});
          }
        },

        handleScroll: ({target}) => {
          const state = this.state;
          const windowScroll = this.attr(`window-scroll`);
          const scrollTop = windowScroll ? window.scrollY : target.scrollTop;
          const scrollBottom = scrollTop + (windowScroll ? window.innerHeight : target.offsetHeight);
          const scrollHeight = windowScroll ? document.body.clientHeight : target.scrollHeight;
          const hasBodyScrolled = scrollTop > 0 && !windowScroll; // TODO maybe add shadow for window scroll

          if (scrollBottom + PROGRESSIVE_HEIGHT_BUFFER_PX >= scrollHeight) {
            const numRowsToDisplay = Math.min(state.numRowsToDisplay, state.rows.length) + PROGRESSIVE_SIZE_ROWS;
            if (numRowsToDisplay !== state.numRowsToDisplay) {
              this.update({numRowsToDisplay});
            }
          }
          if (hasBodyScrolled !== state.hasBodyScrolled) {
            this.update({hasBodyScrolled});
          }
        },

        handleTableScroll: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`tableScroll`, {
              detail: {
                scrollLeft: ev.target.scrollLeft,
              },
            }),
          );
        },

        handleTitleDragStart: (ev, fromIndex) => {
          if (this.attr(`draggable-columns`)) {
            const draggableIndex = this._getDraggableAfterIndex();
            if (fromIndex <= draggableIndex) {
              ev.preventDefault();
              return;
            }

            const tableTitleCell = ev.currentTarget.closest(`.mp-table-title-cell`);
            ev.dataTransfer.setDragImage(tableTitleCell, 0, 0);
            ev.dataTransfer.setData(`text/plain`, fromIndex.toString());
            ev.dataTransfer.effectAllowed = `move`;
            ev.currentTarget.classList.add(`dragging`);
          }
        },

        handleTitleDragEnter: (index) => {
          if (this.attr(`draggable-columns`)) {
            this.update({draggingOverColumnIndex: index});
          }
        },

        handleTitleDragOver: (ev, index) => {
          if (this.attr(`draggable-columns`)) {
            const draggableIndex = this._getDraggableAfterIndex();
            if (index < draggableIndex) {
              return;
            }
            ev.preventDefault();
            ev.dataTransfer.dropEffect = `move`;
            this.update({draggingOverColumnIndex: index, dropPosition: this.getDropPosition(ev)});
          }
        },

        handleTitleStepDragLeave: () => {
          if (this.attr(`draggable-columns`)) {
            this.update({draggingOverColumnIndex: null, dropPosition: null});
          }
        },

        handleTitleStepDragEnd: () => {
          if (this.attr(`draggable-columns`)) {
            this.update({draggingOverColumnIndex: null, dropPosition: null, draggingColumnIndex: null});
          }
        },

        handleTitleDrop: (ev, dropIndex: number) => {
          if (this.attr(`draggable-columns`)) {
            const draggableIndex = this._getDraggableAfterIndex();
            if (dropIndex < draggableIndex) {
              return;
            }

            ev.preventDefault();
            ev.stopPropagation();
            let fromIndex = parseInt(ev.dataTransfer.getData(`text/plain`), 10);
            let toIndex = dropIndex;
            if (this.attr(`multi-select`)) {
              fromIndex--;
              toIndex--;
            }
            if (this.hasExpandableRows()) {
              fromIndex--;
              toIndex--;
            }

            // Insert after the drop target index if dropping below
            if (this.getDropPosition(ev) === DROP_POSITION.LEFT) {
              toIndex--;
            }
            this.helpers.handleTitleStepDragEnd();
            const column = this.state.columns[fromIndex];
            this._moveColumnAfter(fromIndex, toIndex);
            this.dispatchEvent(
              new CustomEvent(`columnMoved`, {
                detail: {
                  fromIndex,
                  toIndex,
                  column,
                  columns: this.state.columns,
                },
              }),
            );
            ev.currentTarget.classList.remove(`dragging`);
          }
        },

        handleTitleMouseDown: (_, fromIndex) => {
          if (this.attr(`draggable-columns`)) {
            this.update({draggingColumnIndex: fromIndex});
          }
        },

        handleTitleMouseUp: () => {
          if (this.attr(`draggable-columns`)) {
            this.update({draggingColumnIndex: null});
          }
        },

        isDroppingLeft: (index) =>
          this.state.draggingOverColumnIndex === index && this.state.dropPosition === DROP_POSITION.LEFT,

        isDroppingRight: (index) =>
          this.state.draggingOverColumnIndex === index && this.state.dropPosition === DROP_POSITION.RIGHT,

        isGroupingEnabled: () => this.state.columns.some((col) => col.isGroupable),

        shiftKeyListener: (ev) => {
          if (!this.helpers.rangeSelectAllowed()) {
            return;
          }

          // show shift+click range select feedback UX
          if (ev.keyCode === SHIFT_KEY_CODE) {
            const doingRangeSelect = ev.type === `keydown` && this.isMouseOver && this.lastSelectedRowIdx !== null;
            if (this.state.doingRangeSelect !== doingRangeSelect) {
              this.update({doingRangeSelect});
            }
          }
        },

        handleTableBodyInserted: (tableBodyEl) => {
          this.tableBodyEl = tableBodyEl;
          this.performResizeRecalculation();
        },

        track: (event, props) => {
          if (this.attr(`track-name`)) {
            this.dispatchEvent(
              new CustomEvent(`track`, {
                bubbles: true,
                composed: true,
                cancelable: true,
                detail: {
                  eventName: `[${COMPONENT_NAME}] ${event}`,
                  props: {
                    name: this.attr(`track-name`),
                    ...props,
                  },
                },
              }),
            );
          }
        },
        hasSlot: (slotName: string): boolean => {
          return !!this.querySelector(`[slot="${slotName}"]`);
        },

        trackLoaded: () => {
          this.helpers.track(TrackingEvent.Loaded, {
            durationMs: getNow() - this.loadingStartTimeMs,
            numRows: this.state.rows && this.state.rows.length,
          });
        },
      },
    };
  }

  public connectedCallback() {
    this.connectedAt = getNow();
    super.connectedCallback();

    const resizeObserver = new ResizeObserver(() => {
      this.performResizeRecalculation();
    });
    resizeObserver.observe(this);
    this.onDisconnected(() => {
      resizeObserver.disconnect();
    });

    if (this.attr(`multi-select`)) {
      // to show shift+click range select feedback
      useEventListener(this, document.body, `keydown`, this.helpers.shiftKeyListener);
      useEventListener(this, document.body, `keyup`, this.helpers.shiftKeyListener);
    }

    if (this.attr(`window-scroll`)) {
      useEventListener(this, window, `scroll`, this.helpers.handleScroll);
    }

    requestAnimationFrame(() => {
      if (!this.attr(`loading`)) {
        this.helpers.trackLoaded();
      }

      if (this.attr(`default-sort-field`) || this.attr(`default-sort-order`)) {
        this._emitSortChange(false);
      }
    });
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `search-filter`) {
      this.resetScroll();
    } else if (attr === `default-sort-field`) {
      // override state from attr if specified
      this.update({sortField: this.attr(attr)});
      this.resetScroll();
      this._emitSortChange(false);
    } else if (attr === `default-sort-order`) {
      this.update({sortOrder: this.attr(attr)});
      this.resetScroll();
      this._emitSortChange(false);
    } else if (attr === `loading`) {
      if (this.attr(attr)) {
        this.loadingStartTimeMs = getNow();
      } else {
        this.helpers.trackLoaded();
      }
    } else if (attr === `multi-select` || attr === `allow-x-overflow`) {
      // these attrs affect the layout width so we need to recalculate where our handles are
      this.performResizeRecalculation();
    }
  }

  public getDropPosition(ev: DragEvent) {
    const dropTarget = ev.currentTarget as HTMLElement;
    const rect = dropTarget.getBoundingClientRect();
    return ev.clientX - (rect.left + rect.width / 2) < 0 ? DROP_POSITION.LEFT : DROP_POSITION.RIGHT;
  }

  public resetScroll() {
    this.displayRowsCache = null;
    this.lastSelectedRowIdx = null;
    this.update({numRowsToDisplay: INITIAL_NUM_DISPLAY_ROWS});

    if (this.attr(`window-scroll`) && this.attr(`sticky-titles`)) {
      window.scrollTo(0, 0);
    } else if (this.tableBodyEl) {
      this.tableBodyEl.scrollTop = 0;
    }
  }

  public getDisplayedRows(): TableRow[] {
    return this._getDisplayRows().map((row) => row.val);
  }

  public hasExpandableRows(): boolean {
    return this.state.rowDetailsRenderer && !this.attr(`disable-expandable-rows`);
  }

  getInitialColumnResizeState(ev: PointerEvent, columnIndex: number): InitialColumnResizeState {
    const tableColumns = this.helpers.getColumnsToDisplay();
    const columnEls: NodeListOf<HTMLElement> = this.el.querySelectorAll(`.mp-table-title-cell`);
    const leftCol = tableColumns[columnIndex];
    const rightCol = tableColumns[columnIndex + 1];
    const leftColEl = columnEls[columnIndex];
    const rightColEl = columnEls[columnIndex + 1];
    const handleEl = ev.target as HTMLElement;
    return {
      x: ev.screenX,
      handle: {el: handleEl, left: handleEl.offsetLeft},
      leftCol: {
        idx: columnIndex,
        col: leftCol,
        el: leftColEl,
        width: leftColEl.offsetWidth,
      },
      rightCol: {
        idx: columnIndex + 1,
        col: rightCol,
        el: rightColEl,
        width: rightColEl?.offsetWidth,
      },
    };
  }

  onResizePointerDown({ev, resizeColumnIndex}: {ev: PointerEvent; resizeColumnIndex: number}) {
    ev.preventDefault();
    this.update({
      resizeColumnIndex,
    });
    const initial = this.getInitialColumnResizeState(ev, resizeColumnIndex);
    this.onResizePointerMove = (ev) =>
      this.handleResizePointerMove({
        initial,
        currentX: ev.screenX,
      });
    this.onResizePointerUp = (ev) => this.handleResizePointerUp({initial, currentX: ev.screenX});
    this.resetResizeState = () => {
      this.cleanResizeEventListeners();
      this.update({
        resizeColumnIndex: null,
      });
    };
    window.addEventListener(`pointermove`, this.onResizePointerMove, true);
    window.addEventListener(`pointerup`, this.onResizePointerUp, true);
    window.addEventListener(`blur`, this.resetResizeState, true);
    window.addEventListener(`click`, this.resetResizeState, true);
  }

  handleResizePointerMove({initial, currentX}: {initial: InitialColumnResizeState; currentX: number}) {
    const {handle, leftCol, rightCol} = initial;
    const {delta, newLeftColWidth, newRightColWidth} = this.calculateNewColumnWidths({initial, currentX});
    handle.el.style.left = `${handle.left + delta}px`;
    // modify the element widths directly instead of going through the event loop for better performance
    this.setColumnWidth(leftCol.idx, newLeftColWidth);

    // when resizing the rightmost column there may be no right col
    newRightColWidth && this.setColumnWidth(rightCol.idx, newRightColWidth);
  }

  handleResizePointerUp({initial, currentX}: {initial: InitialColumnResizeState; currentX: number}) {
    this.resetResizeState();
    const {newLeftColWidth, newRightColWidth} = this.calculateNewColumnWidths({initial, currentX});
    const resizeEvent: ColumnResizeEvent = new CustomEvent(`columnResize`, {
      detail: {
        resizedColumnWidths: {
          ...this.attr(`resized-column-widths`),
          [initial.leftCol.col.field]: newLeftColWidth,
          [initial.rightCol.col.field]: newRightColWidth,
        },
      },
    });
    this.dispatchEvent(resizeEvent);
  }

  cleanResizeEventListeners() {
    window.removeEventListener(`pointermove`, this.onResizePointerMove, true);
    window.removeEventListener(`pointerup`, this.onResizePointerUp, true);
    window.addEventListener(`blur`, this.resetResizeState, true);
    window.addEventListener(`click`, this.resetResizeState, true);
    this.resetResizeState = null;
    this.onResizePointerMove = null;
    this.onResizePointerUp = null;
  }

  calculateNewColumnWidths({initial, currentX}: {initial: InitialColumnResizeState; currentX: number}) {
    // If we are allowing x-overflow the entire table should grow (else steal space from the column to the right)
    const tableGrowthAllowed = this.attr(`allow-x-overflow`);
    const minWidth = 70;
    const delta = currentX - initial.x;
    const minDelta = minWidth - initial.leftCol.width;
    const maxDelta = tableGrowthAllowed ? Number.MAX_VALUE : initial.rightCol?.width - minWidth;
    const clampedDelta = Math.min(maxDelta, Math.max(delta, minDelta));
    const newLeftColWidth = Math.max(initial.leftCol.width + clampedDelta, minWidth);

    const newRightColWidth = tableGrowthAllowed
      ? initial.rightCol?.width
      : Math.max(initial.rightCol.width - clampedDelta, minWidth);
    return {
      delta: Math.floor(clampedDelta),
      newLeftColWidth: Math.floor(newLeftColWidth),
      newRightColWidth: Math.ceil(newRightColWidth),
    };
  }

  setColumnWidth(columnIndex: number, widthPx: number) {
    this.el
      .querySelectorAll(
        `.mp-table-cell:nth-child(${columnIndex + 1}), .mp-table-title-cell:nth-child(${columnIndex + 1})`,
      )
      .forEach((cellEl: HTMLElement) => {
        // we set min/max width instead of width because we're using flexbox
        cellEl.style.minWidth = cellEl.style.maxWidth = `${widthPx}px`;
      });
  }

  /** @param val - pass via prop for perf reasons */
  set rows(val: TableRow[]) {
    const numRowsToDisplay = this.state.numRowsToDisplay || INITIAL_NUM_DISPLAY_ROWS;
    this.update({
      rows: val,
      // clamp numRowsToDisplay between INITIAL_NUM_DISPLAY_ROWS and val.length if num-rows-to-display attr isn't passed in
      numRowsToDisplay: this.attr(`initial-num-rows-to-display`)
        ? this.attr(`initial-num-rows-to-display`)
        : Math.max(Math.min(val.length, numRowsToDisplay), INITIAL_NUM_DISPLAY_ROWS),
    });
    this.displayRowsCache = null;
  }

  set columns(val: TableColumn[]) {
    this.update({columns: val});
    this.displayRowsCache = null;
    this.performResizeRecalculation();
  }

  set rowDetailsRenderer(val: (row: TableRow) => VNode) {
    this.update({rowDetailsRenderer: val});
  }

  private performResizeRecalculation() {
    this.distributeResizableColumns();
    // Recalculating the handle positions is reliant on the DOM layout (queries for actual widths) so we need to wait for a rerender
    requestAnimationFrame(() => this.recalculateResizeHandlePositions());
  }

  /**
   * This function is used to ensure columns take up 100% of the table width.
   * It needs to dynamically calculate columns when:
   * 1. A column does not have an explicit width
   * 2. Columns are added/removed
   * 3. The table changes widths (via window resize for example)
   */
  private _distributeResizableColumns() {
    if (!this.tableBodyEl) {
      return;
    }

    const {columns} = this.state;
    const resizableCols = columns.filter((col) => col.isResizable);
    if (!resizableCols.length) {
      return;
    }

    const resizedColumnWidths = this.attr(`resized-column-widths`) || {};
    const titleEls = this.el.querySelectorAll(`.mp-table-title-cell`);
    const [newResizableCols, existingResizableCols] = partition(
      resizableCols,
      (col) => !resizedColumnWidths[col.field],
    );
    const totalSpace = this.tableBodyEl.clientWidth;

    // If we are alowing x overflow then we can skip the fancy resizing logic
    // and just use the provided widths with a reasonable default (200px)
    if (this.attr(`allow-x-overflow`)) {
      resizableCols.forEach((col) => {
        col.computedWidth = resizedColumnWidths[col.field] || 200;
      });
      this.update({columns});
      return;
    }

    // figure out how much space has been used by non-resizable columns
    const allColumns = this.helpers.getColumnsToDisplay();
    const usedFixedSpace = sum(
      allColumns.map((col, idx) => (!col.isResizable ? (titleEls[idx] as HTMLElement)?.offsetWidth : 0)),
    );

    // figure out how much space we can use for resizable columns
    const availableSpaceForResizableColumns = totalSpace - usedFixedSpace;

    // determine a width to set for columns that don't already have an explicit width set
    const newColWidth = availableSpaceForResizableColumns / resizableCols.length;

    // how much space will be used by new resizable columns
    const spaceUsedByNewColumns = newResizableCols.length * newColWidth;

    // how much space is available for existing resizable columns
    const availableSpaceForExistingColumns = availableSpaceForResizableColumns - spaceUsedByNewColumns;

    // space already being used by existing resizable columns (this could be too much or too little)
    const spaceUsedByExistingColumns = sum(existingResizableCols.map((col) => resizedColumnWidths[col.field]));

    // set explicit widths for each column
    let updatedSpaceUsedByResizableColumns = 0;
    resizableCols.forEach((col) => {
      let computedWidth;
      const resizedColumnWidth = resizedColumnWidths[col.field];
      if (resizedColumnWidth) {
        const relativeWidth = resizedColumnWidth / spaceUsedByExistingColumns;
        computedWidth = Math.floor(relativeWidth * availableSpaceForExistingColumns);
      } else {
        computedWidth = Math.floor(newColWidth);
      }
      updatedSpaceUsedByResizableColumns += computedWidth;
      col.computedWidth = computedWidth;
    });

    // Since we want to use whole pixels, we "floored" the calculated widths. At this
    // point may have a few pixels left to use so we'll distribute them evenly
    let unusedPixels = availableSpaceForResizableColumns - updatedSpaceUsedByResizableColumns;
    for (let i = 0; i < resizableCols.length && unusedPixels > 0; i++) {
      resizableCols[i].computedWidth += 1;
      unusedPixels -= 1;
    }

    this.update({columns});
  }

  /**
   * Positions resize handles by dynamically calculating column widths and offsets
   */
  private _recalculateResizeHandlePositions() {
    if (!this.isConnected) {
      return;
    }

    const resizeHandles = Array.from(this.el.querySelectorAll(`.column-resize-handle`));
    Array.from(this.el.querySelectorAll(`.mp-table-title-cell`)).forEach((cell: HTMLElement, idx) => {
      const resizeHandle = resizeHandles[idx] as HTMLElement;
      resizeHandle.style.left = `${cell.offsetLeft + cell.offsetWidth}px`;
    });
  }

  private _emitSortChange(isHeaderClick: boolean) {
    this.dispatchEvent(
      new CustomEvent(`sortChange`, {
        detail: {
          sortField: this.state.sortField,
          sortOrder: this.state.sortOrder,
          sortIndex: findIndex(this.state.columns, (col) => col.field === this.state.sortField),
          rows: this.getDisplayedRows(),
          isHeaderClick,
        },
      }) as SortChangeEvent,
    );
  }

  // This function moves the 'fromIndex' element just after the 'toIndex' element.
  private _moveColumnAfter(fromIndex: number, toIndex: number) {
    const columns = [...this.state.columns];
    const column = columns[fromIndex];
    columns.splice(fromIndex, 1);
    if (fromIndex < toIndex) {
      toIndex--;
    }
    columns.splice(toIndex + 1, 0, column);

    this.update({columns});
  }

  private _getCellLabel(row: TableRow, col: TableColumn) {
    const label = col.labelTransform ? col.labelTransform(row, col.field) : row[col.field];
    if (label === undefined || label === null) {
      return ``;
    }
    return label.toString();
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private _getDisplayRows(): DisplayTableRow[] {
    // filter + sort is a significant overhead for tables with 1000s of rows
    // we memoize the display rows in a temporary cache so progressive scrolling is cheap
    if (this.displayRowsCache) {
      return this.displayRowsCache;
    }

    const {rows, columns, sortField, sortOrder} = this.state;

    const stickyRows = rows.slice(0, this.attr(`num-sticky-rows`));
    const displayStickyRows = stickyRows.map((val) => ({val, score: 0, matchParts: null}));

    const sortableRows = rows.slice(this.attr(`num-sticky-rows`));
    const searchFilter = this.attr(`search-filter`);
    const searchStr = searchFilter.trim().toLowerCase();
    let displaySortableRows = [];
    if (!searchStr) {
      // fast path if no searchStr
      displaySortableRows = sortableRows.map((val) => ({val, score: 0, matchParts: null}));
    } else {
      // iteration is faster than filter + map
      const searchableCols = columns.filter((col) => col.isSearchable);
      for (const val of sortableRows) {
        const matchParts = {};
        let score = 0;

        // try matching every searchable field
        for (const col of searchableCols) {
          const field = col.field;
          if (col.searchTransform) {
            const match = col.searchTransform(val, col.field, searchStr);
            if (match) {
              matchParts[field] = match.matchParts;
              score = Math.max(match.score, score);
            }
          } else {
            const targetStr = this._getCellLabel(val, col);
            const targetSanitizedStr = targetStr.toLowerCase();
            const match = fuzzyMatchSanitized(targetSanitizedStr, searchStr);

            if (match) {
              matchParts[field] = matchPartsFromSegments(targetStr, match.segments);
              score = Math.max(match.score, score);
            }
          }
        }

        if (score) {
          displaySortableRows.push({val, matchParts, score});
        }
      }
    }

    if (!this.attr(`disable-auto-sorting`) && (searchStr || sortField)) {
      const sortColumn = columns.find((col) => col.field === sortField);
      const sortTransform = sortColumn && sortColumn.sortTransform;

      if (searchStr) {
        // Prioritize manual sort above search ranking when the user defines a search string
        displaySortableRows.sort((a, b) => {
          if (sortField) {
            return this.sortFieldSearch(a, b, sortField, sortOrder);
          }

          const scoreDiff = b.score - a.score;
          if (scoreDiff !== 0) {
            return scoreDiff;
          }

          if (sortTransform) {
            return sortOrder * (sortTransform(a.val, sortField) - sortTransform(b.val, sortField));
          }

          return 0;
        });
      } else {
        // single sort function is faster than lexicalCompose
        // sorting is usually the n*log(n) bottleneck so we want it to be fast
        // prioritize match score, then sortTransform, then alphanumeric, then truthy
        displaySortableRows.sort((a, b) => {
          const scoreDiff = b.score - a.score;
          if (scoreDiff !== 0) {
            return scoreDiff;
          }

          if (sortTransform) {
            return sortOrder * (sortTransform(a.val, sortField) - sortTransform(b.val, sortField));
          }

          if (sortField) {
            return this.sortFieldSearch(a, b, sortField, sortOrder);
          }

          return 0;
        });
      }
    }

    const displayRows = displayStickyRows.concat(displaySortableRows);
    if (displayRows.length !== this.numResults) {
      this.numResults = displayRows.length;
      this.dispatchEvent(
        new CustomEvent(`numResultsChange`, {detail: {numResults: this.numResults}}) as NumResultsChangeEvent,
      );
    }

    return (this.displayRowsCache = displayRows);
  }

  private sortFieldSearch(a, b, sortField, sortOrder) {
    const aVal = a.val[sortField];
    const bVal = b.val[sortField];
    const aType = typeof aVal;
    const bType = typeof bVal;

    if (aType === bType) {
      if (aType === `string`) {
        return sortOrder * aVal.trim().localeCompare(bVal.trim());
      } else if (aType === `number` || aType === `boolean`) {
        return sortOrder * (aVal - bVal);
      }
    }

    // truthy values come before falsey values
    return (aVal ? 0 : 1) - (bVal ? 0 : 1);
  }

  private _getSelectableDisplayedRows(): TableRow[] {
    return this.getDisplayedRows().filter((row) => !row.isSelectDisabled);
  }

  private _getDraggableAfterIndex(): number {
    return this.attr(`draggable-after-index`) || Number.NEGATIVE_INFINITY;
  }
}

export const TableEl = defineAndMakeJsxFactory<TableJsxProps>(COMPONENT_NAME, Table);
export const TableReact = panel2react<TableJsxProps>(COMPONENT_NAME, Table);
